package info.rezident.app.data.user

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.analytics.analytics
import dev.gitlive.firebase.auth.FirebaseUser
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.firestore.js
import info.rezident.app.core.AppTracking
import info.rezident.app.core.AppTracking.USER_PROP_PRICE_VARIANT
import info.rezident.app.core.Logger
import info.rezident.app.data.PriceVariant
import info.rezident.app.data.auth.AuthRepository
import info.rezident.app.data.common.AcademicYearResolver
import info.rezident.app.data.quiz.QuizQuestion
import info.rezident.app.data.quiz.QuizUserAnswers
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.await
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.withContext
import kotlin.js.Date

data class UserProfile(
    val uid: String,
    val priceVariant: PriceVariant?,
    val academicYear: String?,
    val freeRecs: Int?
)

object UserRepository {
    private val firestore: FirebaseFirestore = Firebase.firestore

    private val _user: MutableStateFlow<UserProfile?> = MutableStateFlow(null)
    val user: StateFlow<UserProfile?> = _user.asStateFlow()

    // Field name constants
    private const val FIELD_DATE_CREATED = "dateCreated"
    private const val FIELD_LAST_MODIFIED = "lastModifiedAt"

    // Fields that must not be overwritten on destination if already present
    private val RESERVED_USER_FIELDS: Set<String> =
        setOf("priceVariant", FIELD_DATE_CREATED, "email", FIELD_LAST_MODIFIED)

    // Detect top-level user document: "users/{uid}"
    private fun isMainUserDoc(path: String): Boolean =
        path.startsWith("users/") && path.count { it == '/' } == 1

    suspend fun initUser(user: FirebaseUser?) {
        runCatching {
            val docRef = Firebase.firestore.collection("users")
                .run {
                    if (user != null) {
                        document(user.uid)
                    } else {
                        document
                    }
                }

            // Only set dateCreated if the document doesn't exist or doesn't have it
            val snapshot = docRef.get()
            if (!snapshot.exists || !snapshot.contains(FIELD_DATE_CREATED)) {
                docRef.set(
                    mapOf(FIELD_DATE_CREATED to Date().toISOString()),
                    merge = true
                )
            }

            // Set analytics user ID
            if (user != null) {
                Firebase.analytics.setUserId(user.uid)
            }
        }.onFailure {
            Logger.error("Failed to create user document: $it")
        }
    }

    suspend fun getUserPriceVariant(): PriceVariant = withContext(Dispatchers.Default) {
        // Check if we have a cached variant for the current user
        val currentUid = AuthRepository.getCurrentUser()?.uid
        val cached = _user.value
        if (cached != null && cached.uid == currentUid && cached.priceVariant != null) {
            return@withContext cached.priceVariant
        }

        // If not cached or UID changed, fetch/ensure price variant
        ensureUserSetAndCached()
    }

    suspend fun ensureUserSetAndCached(): PriceVariant = withContext(Dispatchers.Default) {
        val uid = AuthRepository.firebaseUser
            .filter { user ->
                // Wait for a user that has a valid UID (either anonymous or authenticated)
                user?.uid != null && user.uid.isNotBlank()
            }
            .map { it?.uid }
            .firstOrNull()

        if (uid == null) {
            Logger.warn("Authentication timeout or failed - using default price variant")
            return@withContext VariantsRepository.priceVariant()
        }

        Logger.log("Authentication completed. UID: $uid")

        val docRef = firestore.collection("users").document(uid)
        val snapshot = docRef.get()

        val existingVariantName: String? =
            if (snapshot.exists && snapshot.contains("priceVariant")) {
                snapshot.get("priceVariant") as? String
            } else {
                null
            }

        Logger.log("UserRepository.ensurePriceVariantSetAndCached: existing priceVariant in doc='${existingVariantName}'")

        val variant: PriceVariant = if (existingVariantName != null) {
            runCatching { PriceVariant.valueOf(existingVariantName) }
                .onFailure { Logger.warn("Invalid priceVariant value '${existingVariantName}' in user doc; falling back to Remote Config default") }
                .getOrDefault(VariantsRepository.priceVariant())
        } else {
            val resolved = VariantsRepository.priceVariant()
            Logger.log("No priceVariant found in user doc. Writing default from Remote Config: ${resolved.name}")
            docRef.set(
                mapOf("priceVariant" to resolved.name),
                merge = true
            )
            resolved
        }

        // Set the user property in Analytics to track which variant the user has.
        AppTracking.setUserProperty(USER_PROP_PRICE_VARIANT, variant.name)

        // Load existing academicYear if present
        val existingAcademicYear: String? =
            if (snapshot.exists && snapshot.contains("academicYear")) {
                snapshot.get("academicYear") as? String
            } else null

        Logger.log("Caching user profile. uid=$uid, priceVariant=${variant.name}, academicYear=${existingAcademicYear}")
        _user.value = UserProfile(
            uid = uid,
            priceVariant = variant,
            academicYear = existingAcademicYear,
            freeRecs = 0  // No longer used, kept for compatibility
        )
        variant
    }

    /**
     * Resolve academicYear from quiz answers and questions and persist if changed.
     */
    suspend fun resolveAndSetAcademicYear(answers: QuizUserAnswers, questions: List<QuizQuestion>) {
        val resolved: String? = AcademicYearResolver.resolveAcademicYear(answers, questions)
        val currentUid: String? = AuthRepository.getCurrentUser()?.uid
        if (currentUid.isNullOrBlank()) return

        val cached = _user.value
        if (cached != null && cached.uid == currentUid && cached.academicYear == resolved) return

        setAcademicYear(resolved)
    }

    /**
     * Persist academicYear in Firestore (merge), update cache, and set analytics user property.
     */
    suspend fun setAcademicYear(academicYear: String?) {
        val uid = AuthRepository.getCurrentUser()?.uid ?: return
        try {
            val update = buildMap<String, Any> {
                if (academicYear != null) put("academicYear", academicYear)
            }
            firestore.collection("users").document(uid).set(update, merge = true)

            // Update cache preserving other fields
            val current = _user.value
            _user.value = UserProfile(
                uid = uid,
                priceVariant = current?.priceVariant,
                academicYear = academicYear,
                freeRecs = current?.freeRecs,
            )

            // Analytics
            if (academicYear != null) {
                AppTracking.setUserProperty(AppTracking.USER_PROP_ACADEMIC_YEAR, academicYear)
            }
        } catch (e: Exception) {
            Logger.error("Failed to set academicYear: $e")
        }
    }

    /**
     * Sets a merge lease on the current user's document to allow any authenticated user to access it.
     * This must be called BEFORE signing in with a different account, while still authenticated as the source user.
     * The lease expires after 120 seconds.
     *
     * @return `true` if the lease was set successfully, `false` otherwise
     */
    suspend fun setMergeLeaseOnCurrentUser(): Boolean = withContext(Dispatchers.Default) {
        try {
            val currentUid = AuthRepository.getCurrentUser()?.uid
            if (currentUid == null) {
                Logger.error("Cannot set merge lease: no authenticated user")
                return@withContext false
            }

            Logger.log("Setting merge lease on current user $currentUid")

            val leaseExpiration = js("new Date(Date.now() + 120000)") // 120 seconds from now
            val mergeLeaseData = js("{}")
            mergeLeaseData.mergeLease = js("{}")
            mergeLeaseData.mergeLease.expiresAt = leaseExpiration

            val sourceDoc = firestore.collection("users").document(currentUid)
            firestore.runTransaction {
                js.set(sourceDoc.js, mergeLeaseData, js("{ merge: true }"))
            }
            Logger.log("Merge lease set successfully on $currentUid")
            true
        } catch (e: Exception) {
            Logger.error("Error setting merge lease: ${e.message}")
            false
        }
    }

    /**
     * Merges data from anonymous user to authenticated user to preserve user progress.
     *
     * Transfers all user data including quizzes, payments, and preferences using atomic
     * Firestore transactions. Cleans up source document and updates local cache.
     *
     * IMPORTANT: The merge lease must be set on the source document BEFORE calling this function.
     * Call setMergeLease() while still authenticated as the source user, then sign in as target user,
     * then call this function.
     *
     * @param fromUid The UID of the anonymous user (source) to merge data from
     * @param toUid The UID of the authenticated user (destination) to merge data to
     * @return `true` if the merge operation completed successfully, `false` otherwise
     */
    suspend fun mergeUserData(fromUid: String, toUid: String): Boolean = withContext(Dispatchers.Default) {
        try {
            Logger.log("Starting user data merge from $fromUid to $toUid; current UID: ${AuthRepository.getCurrentUser()?.uid}")

            // Move the main user document data
            copyDocument("users/$fromUid", "users/$toUid", deleteSource = false)

            // Step 3: Move all subcollections
            copyCollection("users/$fromUid/checkout_sessions", "users/$toUid/checkout_sessions")
            copyCollection("users/$fromUid/quizzes", "users/$toUid/quizzes")
            copyCollection("users/$fromUid/payments", "users/$toUid/payments")

            // Step 4: Clean up source document
            firestore.collection("users").document(fromUid).delete()
            Logger.log("User $fromUid deleted.")

            // Step 5: Refresh cache
            refreshCacheAfterMerge(toUid)

            Logger.log("Successfully completed data merge from $fromUid to $toUid")
            true
        } catch (e: Exception) {
            Logger.error("Error merging user data from $fromUid to $toUid: ${e.message}; Current UID: ${AuthRepository.getCurrentUser()?.uid}")
            false
        }
    }

    /**
     * Moves or copies a document from source to destination using Firestore transactions.
     *
     * For main user documents, protects existing priceVariant, dateCreated, and email fields.
     * For other documents, performs full merge. Optionally deletes source after merge.
     *
     * @param srcPath The source document path (e.g., "users/abc123")
     * @param dstPath The destination document path (e.g., "users/def456")
     * @param deleteSource Whether to delete the source document after copying (default: true for backward compatibility)
     */
    suspend fun copyDocument(srcPath: String, dstPath: String, deleteSource: Boolean) {
        val firestore = Firebase.firestore
        val src = firestore.document(srcPath)
        val dst = firestore.document(dstPath)

        Logger.log("Copying $src to $dst...")
        if (isMainUserDoc(srcPath) && isMainUserDoc(dstPath)) {
            Logger.log("These are main user docs.")

            firestore.runTransaction {
                val srcSnap = js.get(src.js).await()
                if (!srcSnap.exists()) return@runTransaction

                val dstSnap = js.get(dst.js).await()
                val srcData = srcSnap.data() ?: return@runTransaction
                val dstData = dstSnap.data()

                // clone srcData to mutable "sanitized"
                val sanitized = js("Object.assign({}, srcData)")

                // for each protected field, if dest has it, remove from sanitized
                for (key in RESERVED_USER_FIELDS) {
                    val destHasKey = if (dstData != null)
                        (js("Object.prototype.hasOwnProperty.call(dstData, key)") as? Boolean) ?: true
                    else
                        false
                    if (destHasKey) {
                        js("delete sanitized[key]")
                        Logger.log("Dest has $key")
                    }
                }

                // Remove merge lease from sanitized data (should not be copied to destination)
                js("delete sanitized.mergeLease")

                Logger.log("Merging data to dst: $sanitized...")
                js.set(dst.js, sanitized, js("{ merge: true }"))
                if (deleteSource) {
                    js.delete(src.js)
                }
            }
            Logger.log("Successfully merged user doc.")
            return
        }

        // default behavior for non-main docs
        firestore.runTransaction {
            val srcSnap = js.get(src.js).await()
            if (!srcSnap.exists()) return@runTransaction
            val data = srcSnap.data() ?: return@runTransaction
            js.set(dst.js, data, js("{ merge: true }"))
            if (deleteSource) {
                js.delete(src.js)
            }
        }
    }

    /**
     * Moves all documents in a collection with subcollection support.
     *
     * Transfers entire collection including nested subcollections like quiz answers.
     * Preserves data hierarchy and document relationships during migration.
     *
     * @param srcCollectionPath The source collection path (e.g., "users/abc123/quizzes")
     * @param dstCollectionPath The destination collection path (e.g., "users/def456/quizzes")
     */
    suspend fun copyCollection(srcCollectionPath: String, dstCollectionPath: String, deleteSource: Boolean = true) {
        console.log("Collection: $srcCollectionPath...")
        val firestore = Firebase.firestore
        val srcCollection = firestore.collection(srcCollectionPath)
        console.log("After collection ref")

        val snapshot = srcCollection.get()
        console.log("After snapshot")

        for (doc in snapshot.documents) {
            console.log("Collection doc: ${doc.id}")
            val srcDocPath = "$srcCollectionPath/${doc.id}"
            val dstDocPath = "$dstCollectionPath/${doc.id}"

            // Move the main document
            copyDocument(srcDocPath, dstDocPath, deleteSource)
            Logger.log("Successfully moved document from $srcDocPath to $dstDocPath")

            // Handle subcollections for quizzes (answers)
            if (srcCollectionPath.contains("quizzes") && !srcCollectionPath.contains("answers")) {
                val answersSnapshot = doc.reference.collection("answers").get()
                for (answerDoc in answersSnapshot.documents) {
                    val srcAnswerPath = "$srcDocPath/answers/${answerDoc.id}"
                    val dstAnswerPath = "$dstDocPath/answers/${answerDoc.id}"
                    copyDocument(srcAnswerPath, dstAnswerPath, deleteSource)
                }
                Logger.log("Moved answers")
            }
        }
        console.log("Successfully copied collection to $dstCollectionPath...")
    }

    /**
     * Updates the user document with email address after successful authentication.
     *
     * Uses Firestore merge to preserve existing fields, updates email and timestamp.
     * Silently fails if no current user exists. Used after registration and linking.
     *
     * @param email The email address to store in the user document
     */
    suspend fun updateUserEmail(email: String) {
        try {
            val currentUser = AuthRepository.getCurrentUser()
            val uid = currentUser?.uid ?: return

            firestore.collection("users").document(uid).set(
                mapOf("email" to email),
                merge = true
            )

            Logger.log("Updated user document with email: $email")
        } catch (e: Exception) {
            Logger.error("Error updating user email: $e")
        }
    }

    /**
     * Refreshes the cached user profile after a successful data merge.
     *
     * Updates the cached UID to match the new authenticated user while preserving
     * the price variant if available, or clearing cache to force fresh fetch.
     *
     * @param newUid The UID of the user after merge
     */
    private fun refreshCacheAfterMerge(newUid: String) {
        val currentCache = _user.value
        val preservedVariant = currentCache?.priceVariant

        if (preservedVariant != null) {
            // Preserve the existing price variant and academic year with new UID
            _user.value = UserProfile(
                uid = newUid,
                priceVariant = preservedVariant,
                academicYear = currentCache.academicYear,
                freeRecs = currentCache.freeRecs,
            )
            Logger.log("Updated cached user profile with new UID $newUid and preserved price variant")
        } else {
            // Clear cache to force fresh fetch with new UID
            _user.value = null
            Logger.log("Cleared cache to fetch fresh data for new UID $newUid")
        }
    }
}